import { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import type { OrphanCargo, OrphanListResponse } from "../api/types.ts"
import { useApiQuery, useDebounced } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { ORPHAN_STATUSES, orphanListQuery, orphanStatusOf } from "../lib/orphan-list.ts"
import { formatTracking } from "../lib/tracking.ts"
import { formatDateTime, nullable } from "../components/format.ts"
import { StatusBadge } from "../components/badges.tsx"
import { useCaseDetailPanel } from "../components/CaseDetailPanel.tsx"
import { useOrphanLink } from "../components/scan/useOrphanLink.tsx"
import {
  Badge,
  Button,
  Card,
  ChipGroup,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  FilterBar,
  Input,
  PageHeader,
  Pagination,
  SkeletonTable,
  type Column,
} from "../ui/index.ts"

/** 반품 목록과 같은 300ms. 스캐너가 번호를 한 번에 쏟아부어도 한 번만 찾는다. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * 무적화물만 보는 화면.
 *
 * 입고 스캔 화면의 「무적화물」 카드는 찍는 중에 눈에 걸리는 **지금 처리할 상자**만
 * 보여 준다(안 이은 것, 최근 순). 여기서는 같은 표를 조건·검색·쪽나누기로 되짚고,
 * 이은 기록까지 본다 — 상자가 언제 들어와 어느 건이 됐는지가 나중의 근거다.
 *
 * **잇기는 두 화면이 같은 것을 쓴다**(`components/scan/useOrphanLink.tsx` → 같은 옆판 →
 * 같은 `POST /api/scan/orphans/:id/link`). 목록도 같은 라우트다(`GET /api/scan/orphans`).
 * 그래서 어느 화면에서 이어도 결과가 같고, 한쪽에만 보이는 상자가 생기지 않는다.
 *
 * 일반 입고 화물은 애초에 이 표에 줄이 없다 — 접수된 건으로 입고된 상자는 무적화물이 아니다.
 */
export function OrphanCargoPage() {
  const { t, locale } = useI18n()
  // 조건은 URL 에 둔다(반품 목록과 같은 규칙) — 뒤로가기·링크 공유가 그냥 된다.
  const [params, setParams] = useSearchParams()
  const urlQuery = params.get("q") ?? ""

  const [search, setSearch] = useState(urlQuery)
  const settledSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)

  const query = useApiQuery<OrphanListResponse>(`/api/scan/orphans?${orphanListQuery(params)}`)
  const data = query.data
  const items = data?.items ?? []

  const update = useCallback(
    (changes: Record<string, string | null>) =>
      setParams((current) => {
        const next = new URLSearchParams(current)
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === "") next.delete(key)
          else next.set(key, value)
        }
        // 조건이 바뀌면 첫 쪽부터 본다 — 3쪽을 보다 조건을 좁히면 없는 쪽이 열린다.
        if (!("page" in changes)) next.delete("page")
        return next
      }),
    [setParams],
  )

  // 뒤로가기처럼 바깥에서 q 가 바뀌면 입력칸을 맞춘다(렌더 중에 맞춘다 — 반품 목록과 같다).
  const [syncedQuery, setSyncedQuery] = useState(urlQuery)
  if (syncedQuery !== urlQuery) {
    setSyncedQuery(urlQuery)
    setSearch(urlQuery)
  }

  // 반대로 입력이 멎으면 URL 에 싣는다. 아직 멎지 않았으면 기다린다.
  useEffect(() => {
    if (search !== settledSearch) return
    if (settledSearch !== urlQuery) update({ q: settledSearch })
  }, [search, settledSearch, urlQuery, update])

  const linking = useOrphanLink({ onLinked: () => void query.reload() })

  // 이은 건은 여기서 바로 열어 본다. 제목은 표가 이미 들고 있는 값으로 만든다.
  const detail = useCaseDetailPanel({
    rows: items.flatMap((row) =>
      row.resolvedCase === null
        ? []
        : [{ id: row.resolvedCase.id, seq: row.resolvedCase.seq, orderNo: row.resolvedCase.orderNo }],
    ),
  })

  const page = Number(params.get("page") ?? "1")
  const size = data?.size ?? 50
  const status = orphanStatusOf(params)

  const columns: Column<OrphanCargo>[] = [
    {
      key: "trackingNo",
      header: t("orphans.trackingNo"),
      primary: true,
      render: (row) => <strong>{formatTracking(row.trackingNo)}</strong>,
    },
    {
      key: "linkState",
      header: t("orphans.linkState"),
      render: (row) =>
        row.resolvedAt === null ? (
          <Badge tone="warn" icon="clock">
            {t("orphans.open")}
          </Badge>
        ) : (
          <Badge tone="ok" icon="check">
            {t("orphans.resolved")}
          </Badge>
        ),
    },
    {
      key: "carrier",
      header: t("case.carrier"),
      // 번호만으로는 사가와·야마토를 가릴 수 없다. 이은 건에 적힌 택배사만 보여 주고,
      // 아직 모르면 모른다고 적는다 — 둘 중 하나를 짐작해 적으면 그게 근거로 남는다.
      render: (row) =>
        row.resolvedCase?.carrier ? (
          t(`carrier.${row.resolvedCase.carrier}`)
        ) : (
          <span className="muted">{t("orphans.carrierUnknown")}</span>
        ),
    },
    {
      key: "lastSeenAt",
      header: t("orphans.lastSeenAt"),
      render: (row) => (
        <>
          <time dateTime={row.lastSeenAt}>{formatDateTime(row.lastSeenAt)}</time>
          {row.scanCount > 1 ? (
            <span className="muted"> · {t("scan.orphanCount", { count: row.scanCount })}</span>
          ) : null}
        </>
      ),
    },
    {
      key: "firstSeenAt",
      header: t("orphans.firstSeenAt"),
      priority: "md",
      render: (row) => <time dateTime={row.firstSeenAt}>{formatDateTime(row.firstSeenAt)}</time>,
    },
    {
      key: "resolvedCase",
      header: t("orphans.resolvedCase"),
      render: (row) =>
        row.resolvedCase === null ? (
          "—"
        ) : (
          <>
            <Link to={`/cases/${row.resolvedCase.id}`} onClick={(event) => event.stopPropagation()}>
              #{row.resolvedCase.seq} · {row.resolvedCase.orderNo}
            </Link>
            <span className="muted">
              {" "}
              {row.resolvedCase.customerName} ·{" "}
              {locale === "ja" ? row.resolvedCase.orgNameJa : row.resolvedCase.orgNameKo}
            </span>
          </>
        ),
    },
    {
      key: "caseStatus",
      header: t("orphans.caseStatus"),
      priority: "md",
      render: (row) => (row.resolvedCase === null ? "—" : <StatusBadge status={row.resolvedCase.status} />),
    },
    {
      key: "resolvedAt",
      header: t("orphans.resolvedAt"),
      priority: "lg",
      render: (row) => (row.resolvedAt === null ? "—" : formatDateTime(row.resolvedAt)),
    },
    {
      key: "firstSeenByName",
      header: t("orphans.firstSeenBy"),
      priority: "lg",
      render: (row) => nullable(row.firstSeenByName),
    },
    {
      key: "action",
      header: t("orphans.action"),
      align: "end",
      render: (row) =>
        row.resolvedAt === null ? (
          <Button
            size="sm"
            onClick={(event) => {
              // 줄을 누르면 옆판이 열리므로, 버튼이 그 클릭까지 겹쳐 받지 않게 막는다.
              event.stopPropagation()
              linking.start(row)
            }}
          >
            {t("orphan.link")}
          </Button>
        ) : null,
    },
  ]

  return (
    <>
      <PageHeader
        title={t("orphans.title")}
        description={t("orphans.description")}
        meta={data ? t("orphans.count", { count: data.total }) : null}
      />

      <Card>
        <FilterBar>
          <div className="filter-bar__search">
            <Field label={t("orphans.search")} htmlFor="orphan-list-search">
              <Input
                id="orphan-list-search"
                icon="search"
                autoComplete="off"
                placeholder={t("orphans.searchPlaceholder")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Field>
          </div>

          <Field label={t("orphans.linkState")}>
            <ChipGroup
              label={t("orphans.linkState")}
              value={status}
              allLabel={t("cases.filterAll")}
              options={ORPHAN_STATUSES.map((value) => ({ value, label: t(`orphans.${value}`) }))}
              // 칩의 "전체"(null)는 URL 에 all 로 적는다 — 조건을 지우면 기본값(안 이은 것)으로
              // 돌아가므로, 전체를 고른 것과 아직 아무것도 고르지 않은 것을 가를 수 없다.
              onChange={(value) => update({ status: value ?? "all" })}
            />
          </Field>
        </FilterBar>
      </Card>

      <ErrorNote error={query.error} />

      <Card padded={false}>
        {!data || query.loading ? (
          <div className="table-pad">
            <SkeletonTable rows={6} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            // 안 이은 상자는 누르면 이을 건을 고르는 옆판이 열린다(스캔 화면의 카드와 같다).
            // 이은 상자는 그 건을 열어 본다 — 이미 처리된 줄에서 할 일은 되짚는 것이다.
            onRowClick={(row) =>
              row.resolvedCase === null ? linking.start(row) : detail.open(row.resolvedCase.id)
            }
            empty={
              <EmptyState
                icon="scan"
                title={t("orphans.empty")}
                description={t("orphans.emptyHint")}
              />
            }
            footer={
              items.length > 0 ? (
                <Pagination
                  page={page}
                  size={size}
                  total={data.total}
                  onPage={(next) => update({ page: String(next) })}
                  onSize={(next) => update({ size: String(next), page: null })}
                />
              ) : null
            }
          />
        )}
      </Card>

      {linking.panel}
      {detail.panel}
    </>
  )
}
